import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  TrendingUp,
  ArrowLeft,
  ArrowRight,
  DollarSign,
  Users,
  Globe,
  Target,
  Award,
  BarChart3,
  PieChart,
  LineChart,
  Building2,
  Shield,
  Zap,
  CheckCircle,
  Star,
  Briefcase,
  Clock,
  Calendar,
  Mail,
  Phone,
  FileText,
  Layers,
  Scale,
  TrendingDown,
  Percent,
  CircleDollarSign,
} from "lucide-react";

export default function InvestorPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (hash) {
      // Use requestAnimationFrame to avoid cascading render warning
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
      // Defer state update to next tick
      requestAnimationFrame(() => {
        setActiveSection(hash);
      });
    }
  }, [location]);

  const sections = [
    { id: "overview", title: "Overview", icon: Building2 },
    { id: "problem", title: "The Problem", icon: Target },
    { id: "solution", title: "Our Solution", icon: Zap },
    { id: "market", title: "Market Opportunity", icon: Globe },
    { id: "traction", title: "Traction", icon: TrendingUp },
    { id: "business-model", title: "Business Model", icon: DollarSign },
    { id: "team", title: "Team", icon: Users },
    { id: "investment", title: "Investment", icon: Briefcase },
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-md border-b border-slate-700/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/docs")} className="text-slate-300 hover:text-white hover:bg-slate-700">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Docs
              </Button>
              <div className="h-6 w-px bg-slate-600" />
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-purple-400" />
                <span className="font-semibold text-white">Investor Information</span>
              </div>
            </div>
            <Button 
              onClick={() => window.location.href = "mailto:investors@mymeetingspal.com"}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Mail className="w-4 h-4 mr-2" />
              Contact Us
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-[280px,1fr] gap-8">
          {/* Sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700 p-4">
              <h3 className="font-semibold text-white mb-4">Contents</h3>
              <nav className="space-y-1">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      activeSection === section.id
                        ? "bg-purple-500/20 text-purple-300 font-medium"
                        : "text-slate-400 hover:bg-slate-700/50 hover:text-white"
                    }`}
                  >
                    <section.icon className="w-4 h-4" />
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="min-w-0">
            <div className="bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700 overflow-hidden">
              <div className="p-8 md:p-12">
                {/* Overview */}
                <section id="overview" className="mb-16">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold text-white">Investor Information</h1>
                      <p className="text-slate-400">MyMeetingsPal Investment Opportunity</p>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-purple-500/10 to-violet-500/10 border border-purple-500/20 rounded-2xl p-8 mb-8">
                    <h2 className="text-2xl font-bold text-white mb-4">
                      Transforming Corporate Governance with AI
                    </h2>
                    <p className="text-lg text-slate-300 leading-relaxed">
                      MyMeetingsPal is an AI-powered corporate minutes platform designed for modern governance 
                      teams. We&apos;re revolutionizing how companies capture, manage, and comply with their 
                      corporate meeting obligations — from startups to public companies.
                    </p>
                  </div>

                  {/* Key Metrics */}
                  <div className="grid sm:grid-cols-4 gap-4">
                    <div className="bg-slate-700/50 rounded-xl p-5 text-center">
                      <div className="text-3xl font-bold text-purple-400 mb-1">$50B+</div>
                      <div className="text-sm text-slate-400">TAM</div>
                    </div>
                    <div className="bg-slate-700/50 rounded-xl p-5 text-center">
                      <div className="text-3xl font-bold text-emerald-400 mb-1">85%</div>
                      <div className="text-sm text-slate-400">Gross Margin</div>
                    </div>
                    <div className="bg-slate-700/50 rounded-xl p-5 text-center">
                      <div className="text-3xl font-bold text-blue-400 mb-1">3x</div>
                      <div className="text-sm text-slate-400">YoY Growth</div>
                    </div>
                    <div className="bg-slate-700/50 rounded-xl p-5 text-center">
                      <div className="text-3xl font-bold text-amber-400 mb-1">95%</div>
                      <div className="text-sm text-slate-400">Retention</div>
                    </div>
                  </div>
                </section>

                {/* The Problem */}
                <section id="problem" className="mb-16 pt-8 border-t border-slate-700">
                  <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                    <Target className="w-6 h-6 text-rose-400" />
                    The Problem We&apos;re Solving
                  </h2>

                  <div className="space-y-6 text-slate-300">
                    <p className="text-lg">
                      Corporate minutes are a legal requirement for every registered company. Yet the process 
                      remains stuck in the past:
                    </p>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-5">
                        <Clock className="w-8 h-8 text-rose-400 mb-3" />
                        <h4 className="font-semibold text-white mb-2">Time-Consuming</h4>
                        <p className="text-sm text-slate-400">
                          Secretaries spend 4-8 hours per meeting manually transcribing and formatting minutes.
                        </p>
                      </div>
                      <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-5">
                        <TrendingDown className="w-8 h-8 text-rose-400 mb-3" />
                        <h4 className="font-semibold text-white mb-2">Error-Prone</h4>
                        <p className="text-sm text-slate-400">
                          Manual processes lead to inconsistencies, missing action items, and compliance gaps.
                        </p>
                      </div>
                      <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-5">
                        <Scale className="w-8 h-8 text-rose-400 mb-3" />
                        <h4 className="font-semibold text-white mb-2">Compliance Risk</h4>
                        <p className="text-sm text-slate-400">
                          Poor minute-keeping exposes companies to regulatory penalties and litigation risk.
                        </p>
                      </div>
                      <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-5">
                        <Layers className="w-8 h-8 text-rose-400 mb-3" />
                        <h4 className="font-semibold text-white mb-2">Fragmented Tools</h4>
                        <p className="text-sm text-slate-400">
                          Teams use Word, email, and spreadsheets — no single source of truth for governance.
                        </p>
                      </div>
                    </div>

                    <div className="bg-slate-700/50 rounded-xl p-6 mt-6">
                      <div className="flex items-start gap-4">
                        <div className="text-4xl font-bold text-rose-400">68%</div>
                        <p className="text-slate-300">
                          of company secretaries report that minute-taking is their most time-consuming 
                          administrative task, with public company requirements adding significant 
                          compliance burden.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Our Solution */}
                <section id="solution" className="mb-16 pt-8 border-t border-slate-700">
                  <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                    <Zap className="w-6 h-6 text-emerald-400" />
                    Our Solution
                  </h2>

                  <div className="space-y-6 text-slate-300">
                    <p className="text-lg">
                      MyMeetingsPal is an end-to-end corporate minutes platform that automates the 
                      entire workflow — from recording to compliance-ready documentation.
                    </p>

                    <div className="grid sm:grid-cols-3 gap-4">
                      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-5">
                        <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center mb-3">
                          <FileText className="w-5 h-5 text-emerald-400" />
                        </div>
                        <h4 className="font-semibold text-white mb-2">AI Transcription</h4>
                        <p className="text-sm text-slate-400">
                          Upload audio → get accurate transcript with speaker identification in minutes.
                        </p>
                      </div>
                      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-5">
                        <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center mb-3">
                          <Zap className="w-5 h-5 text-emerald-400" />
                        </div>
                        <h4 className="font-semibold text-white mb-2">Smart Generation</h4>
                        <p className="text-sm text-slate-400">
                          AI generates professionally formatted minutes with action items auto-extracted.
                        </p>
                      </div>
                      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-5">
                        <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center mb-3">
                          <Shield className="w-5 h-5 text-emerald-400" />
                        </div>
                        <h4 className="font-semibold text-white mb-2">Governance Suite</h4>
                        <p className="text-sm text-slate-400">
                          Chair signing, audit trails, compliance linting — built for public companies.
                        </p>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-xl p-6 mt-6">
                      <h4 className="font-semibold text-white mb-3">Key Differentiators</h4>
                      <ul className="space-y-2">
                        <li className="flex items-center gap-3 text-slate-300">
                          <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                          <span>Purpose-built for corporate governance, not general meeting notes</span>
                        </li>
                        <li className="flex items-center gap-3 text-slate-300">
                          <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                          <span>Compliance-first design with audit trails and immutable records</span>
                        </li>
                        <li className="flex items-center gap-3 text-slate-300">
                          <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                          <span>Multi-tenant architecture for company secretarial firms</span>
                        </li>
                        <li className="flex items-center gap-3 text-slate-300">
                          <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                          <span>Enterprise-ready with SSO, RBAC, and legal hold capabilities</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Market Opportunity */}
                <section id="market" className="mb-16 pt-8 border-t border-slate-700">
                  <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                    <Globe className="w-6 h-6 text-blue-400" />
                    Market Opportunity
                  </h2>

                  <div className="space-y-6 text-slate-300">
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-6 text-center">
                        <div className="text-3xl font-bold text-blue-400 mb-2">$50B</div>
                        <div className="text-sm text-slate-400 mb-1">TAM</div>
                        <div className="text-xs text-slate-500">Global GRC Software Market</div>
                      </div>
                      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-6 text-center">
                        <div className="text-3xl font-bold text-blue-400 mb-2">$8.5B</div>
                        <div className="text-sm text-slate-400 mb-1">SAM</div>
                        <div className="text-xs text-slate-500">Board Management Software</div>
                      </div>
                      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-6 text-center">
                        <div className="text-3xl font-bold text-blue-400 mb-2">$1.2B</div>
                        <div className="text-sm text-slate-400 mb-1">SOM</div>
                        <div className="text-xs text-slate-500">AI Meeting Minutes</div>
                      </div>
                    </div>

                    <div className="prose prose-invert max-w-none">
                      <h3>Target Segments</h3>
                      <ul>
                        <li><strong>Public Companies</strong> — Strict compliance requirements, high willingness to pay</li>
                        <li><strong>Company Secretarial Firms</strong> — Multi-tenant needs, manage 100s of entities</li>
                        <li><strong>Mid-Market Companies</strong> — Growing governance needs, seeking efficiency</li>
                        <li><strong>Startups with Investors</strong> — Board meeting requirements, limited resources</li>
                      </ul>

                      <h3>Competitive Landscape</h3>
                      <p>
                        The market is fragmented between general meeting tools (Otter.ai, Fireflies) that lack 
                        governance features, and enterprise board portals (Diligent, BoardEffect) that are 
                        expensive and complex. MyMeetingsPal fills the middle ground with AI-powered 
                        governance-ready minutes at accessible pricing.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Traction */}
                <section id="traction" className="mb-16 pt-8 border-t border-slate-700">
                  <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                    <TrendingUp className="w-6 h-6 text-purple-400" />
                    Traction & Milestones
                  </h2>

                  <div className="space-y-6">
                    <div className="grid sm:grid-cols-4 gap-4">
                      <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-5 text-center">
                        <div className="text-3xl font-bold text-purple-400 mb-1">500+</div>
                        <div className="text-sm text-slate-400">Active Companies</div>
                      </div>
                      <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-5 text-center">
                        <div className="text-3xl font-bold text-purple-400 mb-1">12,000+</div>
                        <div className="text-sm text-slate-400">Minutes Generated</div>
                      </div>
                      <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-5 text-center">
                        <div className="text-3xl font-bold text-purple-400 mb-1">$420K</div>
                        <div className="text-sm text-slate-400">ARR</div>
                      </div>
                      <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-5 text-center">
                        <div className="text-3xl font-bold text-purple-400 mb-1">95%</div>
                        <div className="text-sm text-slate-400">Net Retention</div>
                      </div>
                    </div>

                    <div className="bg-slate-700/50 rounded-xl p-6">
                      <h4 className="font-semibold text-white mb-4">Key Milestones</h4>
                      <div className="space-y-4">
                        <div className="flex items-center gap-4">
                          <div className="w-20 text-sm text-slate-400">Q1 2024</div>
                          <div className="flex-1 text-slate-300">Product launch with core AI transcription</div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-20 text-sm text-slate-400">Q2 2024</div>
                          <div className="flex-1 text-slate-300">100 paying customers, governance features launched</div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-20 text-sm text-slate-400">Q3 2024</div>
                          <div className="flex-1 text-slate-300">Enterprise tier, first public company customer</div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-20 text-sm text-slate-400">Q4 2024</div>
                          <div className="flex-1 text-slate-300">500 customers, $420K ARR, seed round preparation</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Business Model */}
                <section id="business-model" className="mb-16 pt-8 border-t border-slate-700">
                  <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                    <DollarSign className="w-6 h-6 text-emerald-400" />
                    Business Model
                  </h2>

                  <div className="space-y-6 text-slate-300">
                    <p className="text-lg">
                      SaaS subscription model with three tiers designed for different customer segments:
                    </p>

                    <div className="grid sm:grid-cols-3 gap-4">
                      <div className="bg-slate-700/50 rounded-xl p-6">
                        <div className="text-xl font-bold text-white mb-1">Standard</div>
                        <div className="text-2xl font-bold text-emerald-400 mb-3">$199<span className="text-sm text-slate-400">/mo</span></div>
                        <ul className="space-y-2 text-sm text-slate-400">
                          <li>• 10 meetings/month</li>
                          <li>• AI transcription</li>
                          <li>• Basic exports</li>
                          <li>• 10 users</li>
                        </ul>
                      </div>
                      <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-6">
                        <div className="text-xs text-purple-400 font-medium mb-1">MOST POPULAR</div>
                        <div className="text-xl font-bold text-white mb-1">Premium</div>
                        <div className="text-2xl font-bold text-purple-400 mb-3">$599<span className="text-sm text-slate-400">/mo</span></div>
                        <ul className="space-y-2 text-sm text-slate-400">
                          <li>• 40 meetings/month</li>
                          <li>• Governance suite</li>
                          <li>• Chair signing</li>
                          <li>• 25 users</li>
                        </ul>
                      </div>
                      <div className="bg-slate-700/50 rounded-xl p-6">
                        <div className="text-xl font-bold text-white mb-1">Enterprise</div>
                        <div className="text-2xl font-bold text-amber-400 mb-3">Custom</div>
                        <ul className="space-y-2 text-sm text-slate-400">
                          <li>• Unlimited meetings</li>
                          <li>• SSO integration</li>
                          <li>• Full RBAC</li>
                          <li>• Dedicated support</li>
                        </ul>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4 mt-6">
                      <div className="bg-slate-700/50 rounded-xl p-6">
                        <h4 className="font-semibold text-white mb-3">Unit Economics</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-400">Gross Margin</span>
                            <span className="text-emerald-400 font-medium">85%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">ARPU</span>
                            <span className="text-white font-medium">$350/mo</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">CAC</span>
                            <span className="text-white font-medium">$800</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">LTV</span>
                            <span className="text-white font-medium">$12,600</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">LTV:CAC</span>
                            <span className="text-emerald-400 font-medium">15.75x</span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-slate-700/50 rounded-xl p-6">
                        <h4 className="font-semibold text-white mb-3">Revenue Drivers</h4>
                        <ul className="space-y-2 text-sm text-slate-300">
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-emerald-400" />
                            <span>Strong expansion revenue (standard → premium)</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-emerald-400" />
                            <span>Multi-entity expansion within customers</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-emerald-400" />
                            <span>Annual contracts with 17% discount</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-emerald-400" />
                            <span>Partner channel (accounting firms)</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Team */}
                <section id="team" className="mb-16 pt-8 border-t border-slate-700">
                  <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                    <Users className="w-6 h-6 text-blue-400" />
                    Leadership Team
                  </h2>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="bg-slate-700/50 rounded-xl p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-2xl font-bold text-white">
                          MR
                        </div>
                        <div>
                          <div className="font-semibold text-white">Mark Richardson</div>
                          <div className="text-sm text-slate-400">CEO & Co-Founder</div>
                        </div>
                      </div>
                      <p className="text-sm text-slate-300">
                        15 years in corporate governance consulting. Former Company Secretary at ASX-listed 
                        company. Deep understanding of compliance pain points.
                      </p>
                    </div>
                    <div className="bg-slate-700/50 rounded-xl p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center text-2xl font-bold text-white">
                          ST
                        </div>
                        <div>
                          <div className="font-semibold text-white">Sarah Thompson</div>
                          <div className="text-sm text-slate-400">CTO & Co-Founder</div>
                        </div>
                      </div>
                      <p className="text-sm text-slate-300">
                        Ex-Google AI researcher. Led NLP teams building enterprise document intelligence. 
                        PhD in Computational Linguistics from Stanford.
                      </p>
                    </div>
                    <div className="bg-slate-700/50 rounded-xl p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center text-2xl font-bold text-white">
                          JL
                        </div>
                        <div>
                          <div className="font-semibold text-white">James Liu</div>
                          <div className="text-sm text-slate-400">VP Engineering</div>
                        </div>
                      </div>
                      <p className="text-sm text-slate-300">
                        10 years building SaaS platforms. Former engineering lead at DocuSign. 
                        Expertise in security-critical enterprise applications.
                      </p>
                    </div>
                    <div className="bg-slate-700/50 rounded-xl p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-pink-600 rounded-full flex items-center justify-center text-2xl font-bold text-white">
                          AP
                        </div>
                        <div>
                          <div className="font-semibold text-white">Amanda Park</div>
                          <div className="text-sm text-slate-400">VP Sales</div>
                        </div>
                      </div>
                      <p className="text-sm text-slate-300">
                        8 years in enterprise SaaS sales. Built and scaled sales teams at 
                        board management companies. Deep network in governance community.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Investment */}
                <section id="investment" className="mb-8 pt-8 border-t border-slate-700">
                  <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                    <Briefcase className="w-6 h-6 text-amber-400" />
                    Investment Opportunity
                  </h2>

                  <div className="space-y-6 text-slate-300">
                    <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-8">
                      <h3 className="text-xl font-bold text-white mb-4">Seed Round</h3>
                      <div className="grid sm:grid-cols-3 gap-6">
                        <div>
                          <div className="text-3xl font-bold text-amber-400 mb-1">$2.5M</div>
                          <div className="text-sm text-slate-400">Raising</div>
                        </div>
                        <div>
                          <div className="text-3xl font-bold text-white mb-1">$10M</div>
                          <div className="text-sm text-slate-400">Pre-money Valuation</div>
                        </div>
                        <div>
                          <div className="text-3xl font-bold text-white mb-1">20%</div>
                          <div className="text-sm text-slate-400">Equity</div>
                        </div>
                      </div>
                    </div>

                    <div className="prose prose-invert max-w-none">
                      <h3>Use of Funds</h3>
                      <ul>
                        <li><strong>60% Product & Engineering</strong> — Expand AI capabilities, enterprise features</li>
                        <li><strong>25% Sales & Marketing</strong> — Build sales team, conference presence</li>
                        <li><strong>10% Operations</strong> — Compliance certifications, infrastructure</li>
                        <li><strong>5% G&A</strong> — Legal, accounting, administration</li>
                      </ul>

                      <h3>18-Month Milestones</h3>
                      <ul>
                        <li>$2M ARR</li>
                        <li>2,000 paying customers</li>
                        <li>SOC 2 Type II certification</li>
                        <li>UK and EU market expansion</li>
                        <li>Series A readiness</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* CTA */}
                <div className="mt-12 p-8 bg-gradient-to-r from-purple-600 to-violet-600 rounded-2xl text-center">
                  <h3 className="text-2xl font-bold text-white mb-2">Let&apos;s Talk</h3>
                  <p className="text-purple-100 mb-6">
                    We&apos;re excited to share more about our vision and the opportunity.
                  </p>
                  <div className="flex items-center justify-center gap-4">
                    <Button
                      onClick={() => window.location.href = "mailto:investors@mymeetingspal.com"}
                      className="bg-white text-purple-600 hover:bg-purple-50"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Contact Investors
                    </Button>
                    <Button
                      variant="outline"
                      className="border-white text-white hover:bg-purple-700"
                      onClick={() => window.location.href = "tel:+1-888-555-0123"}
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Schedule Call
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
